<template>
  <FriedTable class="dsr-template-picker-table" :aria-label="$t('list-of-templates')">
    <template #header>
      <FriedTableHeader>
        <FriedTableHeaderCell />
        <FriedTableHeaderCell
          :sortable="true"
          class="name-header-cell header-cell"
          data-external="dsr-template-picker-table-sort-name-button"
          :active="isSortingBy(DsrSortAttribute.Title)"
          :direction="sortOrder"
          @click="handleSort(DsrSortAttribute.Title)"
        >
          {{ $t('Name') }}
        </FriedTableHeaderCell>
        <FriedTableHeaderCell v-if="hasTagFlag" class="header-cell hidden-xs-and-down">
          {{ $t('Tags') }}
        </FriedTableHeaderCell>
        <FriedTableHeaderCell
          :sortable="true"
          data-external="dsr-template-picker-table-sort-used-button "
          :active="isSortingBy(DsrSortAttribute.UseCount)"
          class="header-cell hidden-md-and-down"
          :direction="sortOrder"
          @click="handleSort(DsrSortAttribute.UseCount)"
        >
          {{ $t('dsr-content-lib__used') }}
        </FriedTableHeaderCell>
        <FriedTableHeaderCell
          :sortable="true"
          data-external="dsr-template-picker-table-sort-updated-button"
          class="header-cell hidden-sm-and-down"
          :active="isSortingBy(DsrSortAttribute.UpdatedAt)"
          :direction="sortOrder"
          @click="handleSort(DsrSortAttribute.UpdatedAt)"
        >
          {{ $t('last-edited') }}
        </FriedTableHeaderCell>
      </FriedTableHeader>
    </template>
    <template #body>
      <DirectoryRow
        v-for="directory in directories"
        :key="directory.id"
        :data-external="`dsr-template-picker-table-directory-id:${directory.id}-table-row`"
        :directory="directory"
        :has-tag-flag="hasTagFlag"
        @goto-directory="$emit('goto-directory', $event)"
      />
      <TemplateRow
        v-for="template in templates"
        :key="template.id"
        :data-external="`dsr-template-picker-table-template-id:${template.id}-table-row`"
        :template="template"
        :has-tag-flag="hasTagFlag"
        :categories="categories"
        @create-from-template="$emit('create-from-template', $event)"
      />
      <EmptyStateRow v-if="showEmptyState && !loading" />
      <LoaderRow v-else-if="loading" />
      <FriedTableRow v-if="hasNextPage && !loading" ref="loadMoreRow">
        <FriedTableCell colspan="8">
          <FriedSpinner />
        </FriedTableCell>
      </FriedTableRow>
    </template>
  </FriedTable>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, computed, ref } from 'vue';
import type { DsrDirectory } from '@getaccept/lib-shared/src/library/types/dsr-directory';
import type { DsrResource } from '@getaccept/lib-shared/src/library/types/dsr-resource';
import LoaderRow from '@getaccept/lib-shared/src/table/LoaderRow.vue';
import EmptyStateRow from '@getaccept/lib-shared/src/table/EmptyStateRow.vue';
import DirectoryRow from '@getaccept/lib-shared/src/library/components/DirectoryRow.vue';
import { SortOrder } from '@getaccept/fried-tofu';
import { useIntersectionObserver } from '@vueuse/core';
import type { ContentTagCategory } from '@getaccept/dsr-shared';
import { DsrSortAttribute } from '../api/dsr/types/enums/dsr-sort-attribute';
import TemplateRow from './TemplateRow.vue';

export default defineComponent({
  name: 'TemplateTable',
  components: {
    LoaderRow,
    EmptyStateRow,
    DirectoryRow,
    TemplateRow,
  },
  props: {
    templates: {
      type: Array as PropType<DsrResource[]>,
      required: true,
    },
    directories: {
      type: Array as PropType<DsrDirectory[]>,
      required: true,
    },
    sortOrder: {
      type: String as PropType<SortOrder>,
      required: true,
    },
    sortBy: {
      type: String as PropType<DsrSortAttribute>,
      required: true,
    },
    loading: {
      type: Boolean,
    },
    error: {
      type: Boolean,
    },
    hasNextPage: {
      type: Boolean,
    },
    hasTagFlag: {
      type: Boolean,
    },
    categories: {
      type: Array as PropType<ContentTagCategory[]>,
      default: () => [],
    },
  },
  emits: ['create-from-template', 'goto-directory', 'sort', 'fetch-more-templates'],
  setup(props, { emit }) {
    const showEmptyState = computed(
      () => props.error || (!props.templates.length && !props.directories.length)
    );

    const isSortingBy = (key: DsrSortAttribute) => key === props.sortBy;

    const handleSort = (sortBy: DsrSortAttribute) => {
      const isActiveSortBy = props.sortBy === sortBy;
      const toggledSortOrder = props.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
      const sortPayload = { sortBy, sortOrder: isActiveSortBy ? toggledSortOrder : SortOrder.Desc };
      emit('sort', sortPayload);
    };

    const loadMoreRow = ref<HTMLTableRowElement | null>(null);

    useIntersectionObserver(
      loadMoreRow,
      ([{ isIntersecting }]) => {
        if (!isIntersecting || !props.hasNextPage) {
          return;
        }
        emit('fetch-more-templates');
      },
      { threshold: 0 }
    );

    return {
      DsrSortAttribute,
      showEmptyState,
      isSortingBy,
      handleSort,
      loadMoreRow,
    };
  },
});
</script>

<style lang="scss" scoped>
.dsr-template-picker-table {
  box-shadow: none;
  width: 100%;

  .header-cell {
    min-width: 100px;
  }

  .name-header-cell {
    width: 50%;
  }
}
</style>
